import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Check, Infinity as InfinityIcon, Lock, Flame, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";

const PERKS = [
  { icon: InfinityIcon, text: "Unbegrenzte Nachrichten – kein Tageslimit mehr" },
  { icon: Lock, text: "Zugriff auf exklusive VIP-Charaktere" },
  { icon: Flame, text: "VIP-Charaktere biegen die RPG-Regeln – dunklere, mutigere Storys" },
  { icon: Crown, text: "VIP-Abzeichen auf deinem Profil" },
];

export default function VipPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const subscribe = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Checkout konnte nicht gestartet werden");
      setBusy(false);
    }
  };

  const alreadyVip = user?.is_vip;

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <button
          data-testid="vip-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-fuchsia-500/20 border border-amber-400/30 text-xs font-semibold text-amber-300 mb-6">
            <Crown className="w-4 h-4" /> JOYBUZZ VIP
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Werde{" "}
            <span className="bg-gradient-to-r from-amber-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
              VIP
            </span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Unbegrenzte Chats und Zugriff auf exklusive Charaktere, die die Grenzen des Rollenspiels
            neu definieren.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-10 rounded-3xl bg-gradient-to-b from-[#1A1327] to-[#12141D] border border-fuchsia-500/30 p-8 shadow-2xl shadow-fuchsia-500/10"
        >
          <div className="flex items-end justify-center gap-2">
            <span className="font-display text-5xl font-extrabold text-white">15€</span>
            <span className="text-slate-400 mb-2">/ Monat</span>
          </div>

          <div className="mt-8 space-y-4">
            {PERKS.map((p, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`vip-perk-${i}`}>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500/20 to-fuchsia-500/20 border border-fuchsia-400/20 flex items-center justify-center flex-shrink-0">
                  <p.icon className="w-4 h-4 text-fuchsia-300" />
                </div>
                <span className="text-slate-200 text-sm">{p.text}</span>
                <Check className="w-4 h-4 text-emerald-400 ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>

          {alreadyVip ? (
            <div
              data-testid="vip-already-active"
              className="mt-8 w-full text-center bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 font-semibold rounded-xl py-4"
            >
              <Crown className="w-5 h-5 inline mr-2" /> Du bist bereits VIP 🎉
            </div>
          ) : (
            <button
              data-testid="vip-subscribe-btn"
              onClick={subscribe}
              disabled={busy}
              className="mt-8 w-full bg-gradient-to-r from-amber-500 via-fuchsia-600 to-pink-600 hover:opacity-90 text-white font-bold rounded-xl py-4 shadow-lg shadow-fuchsia-500/25 active:scale-95 transition-all disabled:opacity-60"
            >
              {busy ? "Weiterleitung zu Stripe..." : "Jetzt VIP werden – 15€/Monat"}
            </button>
          )}
          <p className="text-center text-xs text-slate-500 mt-4">
            Sichere Zahlung über Stripe · Jederzeit kündbar · Testkarte: 4242 4242 4242 4242
          </p>
        </motion.div>
      </div>
    </div>
  );
}
